import axios, { AxiosError } from "axios";
import { ListItem, ListMeta, ListsPayload } from "./types";

// Thrown instead of firing a request when REACT_APP_AWS_LISTS_API_URL is unset.
// This is what lets the UI ship before the Lambda exists.
export const LISTS_API_NOT_CONFIGURED = "LISTS_API_NOT_CONFIGURED";

type TokenGetter = () => Promise<string>;

export function isListsApiConfigured(): boolean {
  const url = process.env.REACT_APP_AWS_LISTS_API_URL;
  return url != null && url.trim() !== "";
}

function baseUrl(): string {
  if (!isListsApiConfigured()) {
    throw new Error(LISTS_API_NOT_CONFIGURED);
  }
  return process.env.REACT_APP_AWS_LISTS_API_URL!.trim().replace(/\/+$/, "");
}

async function buildHeaders(
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<Record<string, string>> {
  const token = await getRefreshedToken();
  return {
    Authtoken: token,
    "Content-Type": "application/json",
    "x-data-user-id": dataUserId,
  };
}

// Turns anything thrown by the calls below into a message fit for the page's error banner.
export function describeListsError(error: unknown): string {
  if (error instanceof Error && error.message === LISTS_API_NOT_CONFIGURED) {
    return "Lists aren't available yet.";
  }
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401) {
      return "Your session has expired. Please log out and log back in.";
    }
    if (axiosError.response?.status === 599) {
      return "Your subscription has expired, so lists can't be saved.";
    }
  }
  return "Something went wrong saving that change. Please try again.";
}

export async function GetLists(
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<ListsPayload> {
  const url = baseUrl();
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  const res = await axios.get(url, { headers });
  return {
    lists: res.data?.lists ?? [],
    items: res.data?.items ?? [],
  };
}

// Upsert a list's metadata — create or rename.
export async function PostList(
  list: ListMeta,
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<void> {
  const url = baseUrl();
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  await axios.post(url, list, { headers });
}

// Delete a list and every item under it.
export async function DeleteList(
  listId: string,
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<void> {
  const url = `${baseUrl()}/${encodeURIComponent(listId)}`;
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  await axios.delete(url, { headers });
}

// Upsert one item — add, check off, or edit.
export async function PostListItem(
  item: ListItem,
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<void> {
  const url = `${baseUrl()}/${encodeURIComponent(item.ListId)}/items`;
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  await axios.post(url, item, { headers });
}

export async function DeleteListItem(
  listId: string,
  itemId: string,
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<void> {
  const url = `${baseUrl()}/${encodeURIComponent(
    listId
  )}/items/${encodeURIComponent(itemId)}`;
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  await axios.delete(url, { headers });
}

// Bulk delete — powers "Delete Checked".
export async function DeleteListItems(
  listId: string,
  itemIds: string[],
  getRefreshedToken: TokenGetter,
  dataUserId: string
): Promise<void> {
  const url = `${baseUrl()}/${encodeURIComponent(listId)}/items`;
  const headers = await buildHeaders(getRefreshedToken, dataUserId);
  await axios.delete(url, { headers, data: { itemIds } });
}
